/**
 * Wo nennt eine Antwort ein Dokument der Konversation — und welche Seite?
 *
 * Gesucht wird **von den Dokumenten aus**, nicht mit einem Muster über den Text: Die Konversation
 * kennt ihre Dokumente (`fileId`, `filename`), und ihre Namen werden in der Antwort gesucht. So
 * wird ein Name mit Kommas als Ganzes gefunden („2026_06_23_Summary_UFG, Dach und Fach, ….pdf"),
 * und alte Antworten werden ohne Umbau klickbar.
 *
 * Gemessen am 24.09.2026 über 10.488 „Quelle"-Zellen aus 83 Konversationen: 10.263 Nennungen
 * stimmen wörtlich mit einem Dokumentnamen überein, 197 erst nach dem Angleichen (Groß/klein,
 * Leerzeichen, Umlaute — ein beim Hochladen verstümmeltes „Gebäude" steht als „Geb" im Namen —,
 * „(Teil i/n)" aus dem Map/Reduce), 11 nur als Ende eines Abschnittsverweises („Ziff. 5 BB_L.pdf").
 * Nur **3,4 %** nennen überhaupt eine Seite; ohne Seite öffnet der Betrachter auf Seite 1.
 *
 * Rein: kein DOM, kein Netz. Server-Gegenstück: tri-server docs/document-viewer.md.
 */

export interface ConversationDocument {
    fileId: string;
    filename: string;
    webUrl?: string;
    mimeType?: string;
    /**
     * Der Name der Datei im Speicher, wenn er vom angezeigten abweicht. Kommt ein Name im
     * Upload-Ordner schon vor, legt der Speicher die Datei als „00 AzA (1).pdf" ab, die Konversation
     * nennt sie weiter „00 AzA.pdf" — und der Prompt zeigt dem Modell den Speichernamen. Gemessen am
     * 30.09.2026 (Prod, 50 Unterlagen): 38 von 45 „Quelle"-Zellen nannten den Speichernamen und
     * blieben unverlinkt.
     */
    storedName?: string;
}

/** Die Namen, unter denen ein Dokument in einer Antwort stehen kann. */
const namesOf = (document: ConversationDocument): string[] =>
    Array.from(new Set([document.filename, document.storedName].filter((name): name is string => !!name?.trim())));

export interface DocumentMention {
    /** Stelle im Text, [start, end). Umfasst den Namen, nicht die Seitenangabe. */
    start: number;
    end: number;
    document: ConversationDocument;
    /** Die erste genannte Seite, wenn direkt hinter dem Namen eine steht. */
    page?: number;
}

/** Endungen, die als Dateiname zählen, wenn ein Name nur angeglichen passt. */
const FILE_NAME = /[^\s|;,][^|;\n]*?\.(pdf|docx?|xlsx?|pptx?|msg|txt|csv)\b/gi;

/** Angleichen für den zweiten Durchgang: nur Buchstaben, Ziffern und Punkte, ohne Akzente. */
export const normalizeDocumentName = (name: string): string =>
    String(name ?? '')
        .toLowerCase()
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/\(\s*teil\s+\d+\s*\/\s*\d+\s*\)/g, '')
        .replace(/[^a-z0-9.]+/g, '');

/**
 * Angleichen für kaputte Umlaute: Nicht-ASCII-Buchstaben fallen **weg**, statt umschrieben zu werden.
 * Beim Hochladen verstümmelte Namen haben den Umlaut verloren („U-Bahnhfe", „Grnwald", „fr"), und
 * das Modell schreibt ihn mal wieder hin, mal nicht — nur das Weglassen auf beiden Seiten trifft
 * beide Richtungen.
 */
export const asciiDocumentName = (name: string): string =>
    String(name ?? '')
        .toLowerCase()
        .replace(/\(\s*teil\s+\d+\s*\/\s*\d+\s*\)/g, '')
        .replace(/[^a-z0-9.]+/g, '');

/**
 * Die Seite, die direkt hinter einer Nennung steht: „(S. 41, 70)", „S. 41–43", „Seite 4",
 * „Seiten 4 und 5", „Blatt 2", „p. 3". Nur die erste Seite zählt — dort öffnet der Betrachter.
 *
 * Dazwischen darf stehen, was die Nennung nur abschließt: ein Chunk-Zusatz „(Teil 2/5)", den die
 * Zitierregel zwar verbietet, das Modell aber manchmal stehen lässt, und schließende Zeichen wie
 * `"`, `»` oder `)`. Fehlte die Seite dort, öffnete der Betrachter still auf Seite 1.
 */
export const pageAfter = (text: string, from: number): number | undefined => {
    const tail = text.slice(from, from + 60);
    const match =
        /^[\s"“”»'’)\]*_]*(?:\(\s*teil\s+\d+\s*\/\s*\d+\s*\))?\s*[(,:;\-–]?\s*(?:S\.|Seiten?|Blatt|Bl\.|p\.|pp\.|page)\s*(\d{1,4})/i.exec(
            tail
        );
    if (!match) return undefined;
    const page = Number(match[1]);
    return page > 0 ? page : undefined;
};

/**
 * Alle Nennungen von Dokumenten in einem Text, ohne Überlappung, in Textreihenfolge.
 *
 * Ein Name, den mehrere Dokumente **verschiedener** Dateien tragen, wird nicht verlinkt: Welche
 * gemeint ist, weiß niemand, und ein Klick auf die falsche Datei ist schlimmer als keiner.
 */
export const findDocumentMentions = (
    text: string,
    documents: ConversationDocument[],
    /**
     * Was im selben Absatz hinter `text` folgt — steht der Name fett und die Seite dahinter
     * („**X.pdf**, S. 3"), sind das zwei Textknoten, und die Seite steht erst im nächsten.
     */
    following = ''
): DocumentMention[] => {
    if (!text || !documents?.length) return [];
    const usable = documents.filter(d => d?.fileId && d?.filename);
    // Ein Name, den verschiedene Dateien tragen, zeigt auf keine (null) — das gilt über beide Namen
    // eines Dokuments hinweg.
    const index = (keyOf: (name: string) => string) => {
        const map = new Map<string, ConversationDocument | null>();
        const original = new Map<string, string>();
        for (const document of usable) {
            for (const name of namesOf(document)) {
                const key = keyOf(name);
                if (!key) continue;
                const known = map.get(key);
                map.set(key, known === undefined || known?.fileId === document.fileId ? document : null);
                if (!original.has(key)) original.set(key, name);
            }
        }
        return { map, original };
    };
    const byName = index(name => name.toLowerCase()).map;
    const normalized = index(normalizeDocumentName);
    const byNormalized = normalized.map;
    const byAscii = index(asciiDocumentName).map;

    const found: DocumentMention[] = [];
    const taken = (start: number, end: number) =>
        found.some(m => start < m.end && end > m.start);
    const add = (start: number, end: number, document: ConversationDocument) => {
        if (taken(start, end)) return;
        found.push({ start, end, document, page: pageAfter(text + following, end) });
    };

    // 1. Wörtlich — die längsten Namen zuerst, damit „Leistungsverzeichnis_V2.pdf" nicht als
    //    „…V2.pdf" mit dem kürzeren „Leistungsverzeichnis.pdf" verwechselt wird (das es als
    //    Teilzeichenkette ohnehin nicht enthält, aber ein kürzerer Name kann im längeren stehen).
    const lower = text.toLowerCase();
    const names = Array.from(byName.entries())
        .filter(([, document]) => document !== null)
        .sort((a, b) => b[0].length - a[0].length);
    for (const [name, document] of names) {
        let at = lower.indexOf(name);
        while (at >= 0) {
            // Nicht mitten im Wort: „Los1.pdf" ist in „XLos1.pdf" keine Nennung.
            if (at === 0 || !/[\p{L}\p{N}_]/u.test(text[at - 1])) {
                add(at, at + name.length, document as ConversationDocument);
            }
            at = lower.indexOf(name, at + name.length);
        }
    }

    // 2. Angeglichen — Kandidaten mit Dateiendung, die wörtlich nichts getroffen haben.
    FILE_NAME.lastIndex = 0;
    let candidate: RegExpExecArray | null;
    while ((candidate = FILE_NAME.exec(text))) {
        const raw = candidate[0];
        const start = candidate.index + (raw.length - raw.trimStart().length);
        const end = candidate.index + raw.length;
        if (taken(start, end)) continue;
        const bare = raw.replace(/^datei:\s*/i, '');
        const key = normalizeDocumentName(bare);
        const exact = byNormalized.get(key) || byAscii.get(asciiDocumentName(bare));
        if (exact) {
            add(start, end, exact);
            continue;
        }
        // Ein Abschnittsverweis davor („Ziff. 4 Abs. 1 lit. b BB_L.pdf"): genau ein Dokument, dessen
        // Name das Ende bildet — dann gilt nur dieser Teil als Nennung.
        const tails = Array.from(byNormalized.entries()).filter(
            ([name, document]) => document && name.length >= 5 && key.endsWith(name)
        );
        if (tails.length === 1) {
            const [tailKey, tailDocument] = tails[0];
            const document = tailDocument as ConversationDocument;
            const name = normalized.original.get(tailKey) ?? document.filename;
            const tailStart = end - name.length;
            // Der Name muss als eigenes Wort am Ende stehen — „XBB_L.pdf" ist kein Verweis auf
            // „BB_L.pdf" — und dort wirklich stehen, nicht nur angeglichen.
            const standsAlone = tailStart > start && !/[\p{L}\p{N}_]/u.test(text[tailStart - 1]);
            const same = normalizeDocumentName(text.slice(tailStart, end)) === tailKey;
            if (standsAlone && same) add(tailStart, end, document);
        }
    }

    return found.sort((a, b) => a.start - b.start);
};
